import React from "react";

import {
  Box,
  Flex,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
} from "@chakra-ui/react";
import { theme } from "@ovou-inc/ovou-ui";
import { FiMinus, FiPlus } from "react-icons/fi";

const ORANGE = theme.palette.brand.primary.orange;
const TRACK_GREY = theme.palette.ui.greys.grey4;
const ICON_GREY = theme.palette.ui.greys.grey2;

interface IZoomSliderProps {
  minZoom: number;
  maxZoom: number;
  step: number;
  zoom: number;
  setZoom: (zoom: number) => void;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const ZoomSlider: React.FC<IZoomSliderProps> = ({
  minZoom,
  maxZoom,
  step,
  zoom,
  setZoom,
}) => {
  const cannotDecrement = zoom <= minZoom;
  const cannotIncrement = zoom >= maxZoom;

  const handleDecrement = (): void => {
    if (cannotDecrement) return;
    setZoom(clamp(zoom - step, minZoom, maxZoom));
  };

  const handleIncrement = (): void => {
    if (cannotIncrement) return;
    setZoom(clamp(zoom + step, minZoom, maxZoom));
  };

  const handleSliderChange = (value: number): void => setZoom(value);

  return (
    <Flex align="center" w="100%" gap="16px">
      <Box
        as="button"
        type="button"
        display="flex"
        alignItems="center"
        justifyContent="center"
        w="24px"
        h="24px"
        flexShrink={0}
        color={ICON_GREY}
        opacity={cannotDecrement ? 0.4 : 1}
        cursor={cannotDecrement ? "not-allowed" : "pointer"}
        disabled={cannotDecrement}
        _focus={{ outline: "none", boxShadow: "none" }}
        _focusVisible={{ outline: "none", boxShadow: "none" }}
        _active={{ boxShadow: "none" }}
        onClick={handleDecrement}
        aria-label="Zoom out"
      >
        <FiMinus size={24} />
      </Box>

      <Slider
        aria-label="Zoom"
        min={minZoom}
        max={maxZoom}
        step={step}
        value={zoom}
        onChange={handleSliderChange}
        focusThumbOnChange={false}
        flex="1"
      >
        <SliderTrack bg={TRACK_GREY} height="4px" borderRadius="full">
          <SliderFilledTrack bg={ORANGE} />
        </SliderTrack>
        <SliderThumb boxSize={4} bg={ORANGE} border="none" />
      </Slider>

      <Box
        as="button"
        type="button"
        display="flex"
        alignItems="center"
        justifyContent="center"
        w="24px"
        h="24px"
        flexShrink={0}
        color={ICON_GREY}
        opacity={cannotIncrement ? 0.4 : 1}
        cursor={cannotIncrement ? "not-allowed" : "pointer"}
        disabled={cannotIncrement}
        _focus={{ outline: "none", boxShadow: "none" }}
        _focusVisible={{ outline: "none", boxShadow: "none" }}
        _active={{ boxShadow: "none" }}
        onClick={handleIncrement}
        aria-label="Zoom in"
      >
        <FiPlus size={24} />
      </Box>
    </Flex>
  );
};

export default ZoomSlider;
